import type {CSSProperties} from 'react';
import {radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardDoneLook = 'onb-row' | 'onb-row-first' | 'onb-row-missing' | 'onb-row-first-missing' | 'onb-cta';

const ONBOARD_DONE_LOOKS = new Set<string>(['onb-row', 'onb-row-first', 'onb-row-missing', 'onb-row-first-missing', 'onb-cta']);
export const isWedgeOnboardDoneLook = (look: string): look is WedgeOnboardDoneLook => ONBOARD_DONE_LOOKS.has(look);

/* Released onboarding done screen (prep-screens-1 OnboardDone): the confirm rows and the Power up tile action. A row is a full-width flex line
   with a 12px gap and 13px by 14px of padding, a 0.5px separator on top (the first row has none), left-aligned text, the Wedge font and no
   border. A row with a missing required field takes the soft red fill; every other row is transparent. The shipped hover and press rules sit
   on the `wedge-onb-row` class, which the caller keeps. The `border: 0` that follows the top rule is the source order: it resets that rule,
   so a row prints no rule at rest, as released. A row is 46px tall at least (one 19.6px line and 26px of padding) and the rows sit edge to
   edge, so it draws no transparent target. The tile action is a pill on the primary ink with the page fill as its ink, 6px by 12px of
   padding, an 8px radius and 12px bold type with a 0.2 track; the caller keeps the shipped `wedge-pill` class. Each reads the exact legacy
   roles, and the declaration order is the order of the source. The caller owns the labels and the handlers. */
const ROW_GAP = 12, ROW_PADDING = '13px 14px', SEPARATOR_WIDTH = '0.5px', CTA_PADDING = '6px 12px', CTA_FONT = 12, CTA_TRACK = 0.2;

export function wedgeOnboardDoneStyle(look: WedgeOnboardDoneLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'onb-cta') return {
    background: r('label'), color: r('bg'), border: 0, padding: CTA_PADDING, borderRadius: radius.sm,
    fontSize: CTA_FONT, fontWeight: 700, fontFamily: r('font'), cursor: 'pointer', letterSpacing: CTA_TRACK,
  };
  return {
    display: 'flex', alignItems: 'center', gap: ROW_GAP, padding: ROW_PADDING,
    borderTop: look === 'onb-row-first' || look === 'onb-row-first-missing' ? 'none' : `${SEPARATOR_WIDTH} solid ${r('separator')}`,
    width: '100%', textAlign: 'left', background: look.endsWith('-missing') ? r('redSoft') : 'transparent',
    border: 0, cursor: 'pointer', fontFamily: r('font'),
  };
}

/* Only the tile action keeps the transparent target: it is 28px tall and sits under its tile body with 8px of margin and 12px of tile padding below. */
export const wedgeOnboardDoneDrawsHit = (look: string): boolean => !isWedgeOnboardDoneLook(look) || look === 'onb-cta';
